import { useEffect } from "react";

import { EntityId } from "@reduxjs/toolkit";

import {
    Box,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import FitnessCenterOutlinedIcon from "@mui/icons-material/FitnessCenterOutlined";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { RootState } from "app/store";
import { playbackSelectionSelectors } from "./store/selectors.selection";
import { sitesThunks } from "features/sites/store/slice";
import { selectSiteById } from "features/sites/store/selectors";

import { PlaybackRangeControls } from "./PlaybackRangeControls";
import { DeviceListSidebarMenu } from "features/devices/DeviceListSidebarMenu";
import { selectDevicesBySiteIdSortedByName } from "features/devices/store/selectors";
import { DateTime } from "luxon";

export interface SidebarProps {
    siteId: EntityId;
    onToggleDevice: (
        siteId: EntityId,
        deviceId: EntityId,
        selected: boolean,
    ) => void;
    onSetSelectionStateOfAllDevicesInSite: (
        siteId: EntityId,
        selected: boolean,
    ) => void;

    rangeStartDateTime?: string;
    rangeStartDateTimeError?: string;
    rangeEndDateTime?: string;
    rangeEndDateTimeError?: string;
    onRangeChange: (
        startDateTime: DateTime | null,
        endDateTime: DateTime | null,
    ) => void;
}

export function Sidebar(props: SidebarProps) {
    const dispatch = useAppDispatch();

    const site = useAppSelector((state: RootState) =>
        selectSiteById(state, props.siteId.toString()),
    );

    const selectedDeviceIds = useAppSelector(
        playbackSelectionSelectors.selectDeviceIds,
    );

    const devicesSortedByName = useAppSelector((state) =>
        selectDevicesBySiteIdSortedByName(state, props.siteId),
    );

    // make sure site information is available
    useEffect(() => {
        if (!site) {
            dispatch(sitesThunks.fetchSiteAsync(props.siteId));
        }
    }, [dispatch, props.siteId, site]);

    return (
        <Box
            sx={{
                overflow: "auto",
                "&::-webkit-scrollbar": { display: "none" },
            }}
        >
            {site && (
                <List>
                    <ListItemButton>
                        <ListItemIcon>
                            <FitnessCenterOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary={site.name} />
                    </ListItemButton>

                    <PlaybackRangeControls
                        startDateTime={props.rangeStartDateTime}
                        endDateTime={props.rangeEndDateTime}
                        startDateTimeError={props.rangeStartDateTimeError}
                        endDateTimeError={props.rangeEndDateTimeError}
                        onRangeChange={props.onRangeChange}
                    />

                    <DeviceListSidebarMenu
                        activeSiteId={props.siteId}
                        devices={devicesSortedByName}
                        onToggleDevice={props.onToggleDevice}
                        onSetSelectionStateOfAllDevicesInSite={
                            props.onSetSelectionStateOfAllDevicesInSite
                        }
                        selectedDeviceIds={selectedDeviceIds}
                        thumbnailRefreshIntervalInMs={0}
                    />
                </List>
            )}
        </Box>
    );
}
